import React, {useState, useEffect, useCallback} from "react";

import moment from "moment";
import {Card, message, Row, Col, Tag, Button, Statistic, Pagination, Spin} from "antd";
import FontAwesome from "react-fontawesome";

import {Loading, ScrollTable, showNotification} from "src/components/stateless";

import InvoiceForm from "./invoice";

import {updateEarning, fetchPaginatedEarnings, fetchInvoiceStats} from "src/actions/earnings";
import {getChildrensOfId} from "src/actions/users";

function InvoicesList(props) {
  const [visible, setVisible] = useState(false);
  const [invoice, setInvoice] = useState({});
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [invoicesStats, setInvoicesStats] = useState({payed: 0, pending: 0, totalPayed: 0, totalPending: 0});
  const [userChildrens, setUserChildrens] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const {user} = props;

  const showDrawer = (id, amount, ref) => {
    setVisible(true);
    setInvoice({id, amount, ref});
  };

  const onClose = () => {
    setVisible(false);
    setInvoice({});
  };

  const onSubmitPayment = async values => {
    setVisible(false);
    try {
      setLoading(true);
      await updateEarning(values, props.client);
      message.success("Paiement enregistré avec succès!");
      setRefreshKey(k => k + 1);
    } catch (err) {
      message.error("Erreur d'enregistrement!");
      setLoading(false);
    }
  };

  const loadData = useCallback(async (currentPage) => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * pageSize;
      const [result, stats, childrens] = await Promise.all([
        fetchPaginatedEarnings(props.client, {
          limit: pageSize,
          offset,
          type: "Répartition",
        }),
        user.role.name === "Admin" ? fetchInvoiceStats(props.client) : Promise.resolve(null),
        getChildrensOfId(user.id, props.client),
      ]);

      setItems(result.items || []);
      setTotal(result.total || 0);
      setPage(currentPage);
      setUserChildrens(childrens || []);
      if (stats) {
        setInvoicesStats(stats);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showNotification("error", "Erreur", "Impossible de charger les factures");
    }
  }, [pageSize, refreshKey]);

  useEffect(() => {
    loadData(1);
  }, [refreshKey, pageSize]);

  const renderRow = curr => ({
    ...curr,
    key: curr.id,
    tva: curr.user.needTva ? curr.vatAmount + " €" : 0,
    nettyId: curr.transaction.nettyId,
    user: `${curr.from.firstname} ${curr.from.lastname}`,
    userId: curr.user.id,
    from: `${curr.from.firstname} ${curr.from.lastname}`,
    to: `${curr.user.firstname} ${curr.user.lastname}`,
    date: moment(curr.createdAt).format("DD/MM/YYYY HH:mm:ss"),
    amount: curr.amount + " €",
    profit: curr.user.needTva
      ? `${parseFloat(curr.amount - curr.vatAmount).toFixed(2)} €`
      : `${curr.amount} €`,
    commissionAmount: parseFloat(curr.transaction.commissionAmount).toFixed(2) + " €",
    state: curr.paymentDate ? (
      <Tag color="green">
        Réglement de {curr.payedAmount} € effectué le{" "}
        {moment(curr.paymentDate).format("DD/MM/YYYY")}
      </Tag>
    ) : (
      <div>
        <Tag color="red">En attente de paiement</Tag>
        {user.role.name === "Admin" && (
          <Button
            icon={<FontAwesome name="money-bill-alt" style={{color: "rgb(88, 196, 26)", marginRight: 5}} />}
            size="small"
            onClick={() =>
              showDrawer(
                curr.id,
                curr.user.needTva ? parseFloat(curr.amount - curr.vatAmount).toFixed(2) : curr.amount,
                curr.invoiceId
              )
            }
          >
            Payer
          </Button>
        )}
      </div>
    ),
  });

  if (loading && items.length === 0) {
    return <Loading />;
  }

  // Filter items by role
  let gainsList = items.filter(g => g.level !== -1 && g.level !== 0);
  if (user.role.name !== "Admin") {
    gainsList = gainsList.filter(g =>
      userChildrens.find(id => id === g.transaction.user.id)
    );
  }

  const list = gainsList.map(t => renderRow(t));

  const subColumns = [
    {
      dataField: "invoiceId", text: "Réf.", sort: true,
      sortValue: (cell) => parseInt(cell.substring(8)),
      sortCaret: (order) => {
        if (!order) return <span>  Desc/Asc</span>;
        if (order === "asc") return <span style={{float: "right"}}><FontAwesome name="caret-up" /></span>;
        if (order === "desc") return <span style={{float: "right"}}><FontAwesome name="caret-down" /></span>;
        return null;
      },
    },
    {dataField: "nettyId", text: "Id du bien netty"},
    {dataField: "transaction.commissionAmountHt", text: "Commission HT", formatter: e => `${parseFloat(e).toFixed(2)} €`},
    {dataField: "date", text: "Date"},
    {dataField: "from", text: "De", searchable: false},
    {dataField: "to", text: "Bénéficiaire"},
    {dataField: "type", text: "Type"},
    {dataField: "profit", text: "Honoraires HT"},
    {dataField: "state", text: "Status"},
  ];

  const defaultSorted = [{dataField: "invoiceId", order: "desc"}];

  return (
    <div>
      <Row>
        <Col span={24}>
          <Row gutter={16} style={{marginTop: 20}}>
            <Col span={24}>
              {user.role.name === "Admin" && (
                <Card
                  style={{marginBottom: 20}}
                  title={<h5><FontAwesome name="chart-line" style={{color: "#1890ff"}} /> Statistiques</h5>}
                >
                  <div className="flex-container">
                    <div className="flex-item flex-item-1">
                      <Statistic
                        title="Factures en attente"
                        value={invoicesStats.countPending}
                        prefix={<FontAwesome name="clock" style={{color: "#EE0505"}} />}
                      />
                    </div>
                    <div className="flex-item flex-item-2">
                      <Statistic
                        title="Total factures en attente"
                        value={`${parseFloat(invoicesStats.totalPending / (1 + 20 / 100)).toFixed(2)} €`}
                      />
                    </div>
                    <div className="flex-item flex-item-3">
                      <Statistic
                        title="Factures réglées"
                        value={invoicesStats.countPayed}
                        prefix={<FontAwesome name="clipboard-check" style={{color: "#52C41A"}} />}
                      />
                    </div>
                    <div className="flex-item flex-item-4">
                      <Statistic
                        title="Total facturé"
                        value={`${parseFloat(invoicesStats.totalPayed / (1 + 20 / 100)).toFixed(2)} €`}
                      />
                    </div>
                  </div>
                </Card>
              )}
              <Card title={<h5><FontAwesome name="list" style={{color: "#1890ff"}} /> Liste des factures</h5>}>
                <Spin spinning={loading}>
                  <ScrollTable
                    search
                    list={list}
                    sizePerPage={pageSize}
                    columns={subColumns}
                    {...props}
                    defaultSorted={defaultSorted}
                    hidePager
                  />
                </Spin>
                <div style={{display: "flex", justifyContent: "flex-end", padding: "12px 0"}}>
                  <Pagination
                    current={page}
                    total={total}
                    pageSize={pageSize}
                    onChange={(p) => loadData(p)}
                    onShowSizeChange={(_, size) => setPageSize(size)}
                    showSizeChanger
                    showTotal={(total) => `${total} factures`}
                    pageSizeOptions={["10", "25", "50", "100"]}
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      {visible && (
        <InvoiceForm
          visible={visible}
          onClose={onClose}
          invoice={invoice}
          onSubmitPayment={onSubmitPayment}
        />
      )}
    </div>
  );
}

export default InvoicesList;
